import { ref, onValue, update, set, get, push, remove, child } from 'firebase/database'
import { MatchData } from '../types/match'
import { database, firestore, auth } from '../config/firebase'

export const createMatch = async (matchData: MatchData): Promise<{matchId: string, hostControllerToken: string}> => {
  try {
    const matchesRef = ref(database, 'matches')
    const newMatchRef = push(matchesRef)
    const now = Date.now()
    const expiresAt = now + (24 * 60 * 60 * 1000) // 24 hours from now
    
    const matchToCreate = {
      ...matchData,
      id: newMatchRef.key,
      createdAt: now,
      expiresAt: expiresAt,
      status: 'active',
      scores: {
        team1: 0,
        team2: 0,
        updatedAt: now
      },
      setScores: [],
      gameScores: [], // Initialize gameScores for tennis matches
      totalCompletedGames: 0, // Initialize cumulative game counter for side-swapping
      events: {},
      cooldownDuration: 2.5,
      showQRCode: matchData.showQRCode ?? true, // Default to true if not specified
      // Court tagging fields
      createdBy: matchData.createdBy,
      userEmail: matchData.userEmail,
      courtNumber: matchData.courtNumber,
      planType: matchData.planType,
      maxCourts: matchData.maxCourts
    }
    
    await set(newMatchRef, matchToCreate)
    
    if (!newMatchRef.key) {
      throw new Error('Failed to get match ID from Firebase')
    }
    
    // Generate controller token for the host
    const { generateNewControllerToken } = await import('../utils/controllerTokenManager')
    const hostControllerToken = await generateNewControllerToken(newMatchRef.key)
    
    return {
      matchId: newMatchRef.key,
      hostControllerToken
    }
  } catch (error) {
    throw new Error('Failed to create match. Please check your Firebase configuration.')
  }
}

export const getMatch = async (matchId: string) => {
  const matchRef = ref(database, `matches/${matchId}`)
  try {
    const snapshot = await get(matchRef)
    if (snapshot.exists()) {
      return snapshot.val()
    }
    return null
  } catch (error) {
    console.error('Error getting match:', error)
    return null
  }
}

export const subscribeToMatch = (matchId: string, callback: (data: any) => void) => {
  const matchRef = ref(database, `matches/${matchId}`)
  return onValue(matchRef, (snapshot) => {
    const data = snapshot.val()
    callback(data)
  })
}

export const updateScore = async (
  matchId: string,
  team1Score: number,
  team2Score: number,
  lastScoringTeam: 'team1' | 'team2' | null = null
) => {
  const matchRef = ref(database, `matches/${matchId}`)
  
  try {
    // Get current match data to check cooldown
    const snapshot = await get(matchRef)
    if (!snapshot.exists()) {
      throw new Error('Match not found')
    }
    
    const matchData = snapshot.val()
    const currentScores = matchData.scores || {}
    const lastUpdateTime = currentScores.updatedAt || 0
    const cooldownDuration = matchData.cooldownDuration || 2.5
    const currentTime = Date.now()
    
    // Check if enough time has passed since the last update
    if (currentTime - lastUpdateTime < cooldownDuration * 1000) {
      const remainingCooldown = Math.ceil((cooldownDuration * 1000 - (currentTime - lastUpdateTime)) / 1000)
      throw new Error(`Score update blocked: ${remainingCooldown} second cooldown remaining`)
    }
    
    // Proceed with the update
    await update(matchRef, {
      scores: {
        team1: team1Score,
        team2: team2Score,
        updatedAt: currentTime,
        cooldown: cooldownDuration,
        lastScoringTeam
      }
    })
  } catch (error: any) {
    throw error
  }
}

export const addScoreEvent = async (
  matchId: string,
  team: 'team1' | 'team2',
  points: number,
  type?: 'game' | 'set'
) => {
  const matchRef = ref(database, `matches/${matchId}`)
  const matchData = await get(matchRef)
  const events = matchData.val()?.events || {}
  
  await update(matchRef, {
    events: {
      ...events,
      [`event_${Date.now()}`]: {
        team,
        points,
        timestamp: Date.now(),
        ...(type && { type })
      }
    }
  })
}

export const undoLastScore = async (matchId: string) => {
  const matchRef = ref(database, `matches/${matchId}`)
  const matchData = await get(matchRef)
  const data = matchData.val()
  
  if (!data) {
    throw new Error('Match not found')
  }
  
  const currentScores = data.scores || {}
  const lastUpdateTime = currentScores.updatedAt || 0
  const cooldownDuration = data.cooldownDuration || 2.5
  const currentTime = Date.now()
  
  // Check if enough time has passed since the last update
  if (currentTime - lastUpdateTime < cooldownDuration * 1000) {
    const remainingCooldown = Math.ceil((cooldownDuration * 1000 - (currentTime - lastUpdateTime)) / 1000)
    throw new Error(`Score update blocked: ${remainingCooldown} second cooldown remaining`)
  }
  
  const events = data.events || {}
  
  // Get the last event
  const eventKeys = Object.keys(events)
  if (eventKeys.length > 0) {
    const lastEventKey = eventKeys[eventKeys.length - 1]
    const lastEvent = events[lastEventKey]
    const { [lastEventKey]: _, ...remainingEvents } = events

    // Calculate new scores based on the last event
    let newTeam1Score = currentScores.team1
    let newTeam2Score = currentScores.team2

    if (lastEvent.team === 'team1') {
      newTeam1Score = Math.max(0, newTeam1Score - lastEvent.points)
    } else if (lastEvent.team === 'team2') {
      newTeam2Score = Math.max(0, newTeam2Score - lastEvent.points)
    }

    // Update both the events and scores
    await update(matchRef, {
      events: remainingEvents,
      scores: {
        team1: newTeam1Score,
        team2: newTeam2Score,
        updatedAt: currentTime,
        cooldown: cooldownDuration,
        lastScoringTeam: null
      }
    })
  }
}

export async function updateMatch(matchId: string, updates: Record<string, any>) {
  // Fetch current match data to get required fields
  const current = await getMatch(matchId)
  if (!current) throw new Error('Match not found for updateMatch')
  const requiredFields = {
    scoringStructure: current.scoringStructure,
    team1Color: current.team1Color,
    team2Color: current.team2Color,
    pointsPerSet: current.pointsPerSet,
    bestOf: current.bestOf,
    winByTwo: current.winByTwo
  }
  const merged = { ...requiredFields, ...updates }
  return update(ref(database, `matches/${matchId}`), merged)
}

export const updateTiebreakPointWithCooldown = async (
  matchId: string,
  team: 'team1' | 'team2',
  increment: number = 1
) => {
  const matchRef = ref(database, `matches/${matchId}`)
  const matchDataSnap = await get(matchRef)
  const matchData = matchDataSnap.val()
  if (!matchData) throw new Error('Match not found')

  const cooldownDuration = matchData.cooldownDuration || 2.5
  const lastUpdateTime = matchData.tiebreakPointsUpdatedAt || 0
  const currentTime = Date.now()

  if (currentTime - lastUpdateTime < cooldownDuration * 1000) {
    const remainingCooldown = Math.ceil((cooldownDuration * 1000 - (currentTime - lastUpdateTime)) / 1000)
    throw new Error(`Tiebreak point update blocked: ${remainingCooldown} second cooldown remaining`)
  }

  const tiebreakPoints = matchData.tiebreakPoints || { team1: 0, team2: 0 }
  const newTiebreakPoints = {
    ...tiebreakPoints,
    [team]: (tiebreakPoints[team] || 0) + increment
  }

  // Determine if this point wins the tiebreak (7+ and 2-point lead)
  const tiebreakTarget = 7
  const p1 = team === 'team1' ? newTiebreakPoints.team1 : newTiebreakPoints.team1
  const p2 = team === 'team2' ? newTiebreakPoints.team2 : newTiebreakPoints.team2
  const hasWinner =
    (newTiebreakPoints.team1 >= tiebreakTarget || newTiebreakPoints.team2 >= tiebreakTarget) &&
    Math.abs(newTiebreakPoints.team1 - newTiebreakPoints.team2) >= 2

  // Add event for this tiebreak point
  if (hasWinner) {
    await addScoreEvent(matchId, team, increment, 'game')
  } else {
    await addScoreEvent(matchId, team, increment)
  }

  await update(matchRef, {
    tiebreakPoints: newTiebreakPoints,
    tiebreakPointsUpdatedAt: currentTime,
    'scores/lastScoringTeam': team,
    'scores/cooldown': cooldownDuration
  })
}

export { database, firestore, auth } 